import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from './context/AuthContext';
import api from './api';
import SwitchUserModal from './components/SwitchUserModal';
import SuggestPanel, { takePicture } from './components/SuggestPanel';
import { ViewAsPicker } from './components/ViewAs';
import Logo from './components/Logo';

// The same menu for everyone, and each button shows what your role gets (the
// director, 3 Nov 2026). Admin is a director's word; the floor's rest is More.
// On a phone there is room for seven: FAQ is a tile inside Admin or More.
const linksFor = (director, phone) => [
  { path: '/today',    label: 'Today',   icon: 'fa-solid fa-clock' },
  { path: '/schedule', label: director ? 'Schedule' : 'Board', icon: 'fa-solid fa-calendar-days' },
  { path: '/orders',   label: 'Orders',  icon: 'fa-solid fa-boxes-stacked' },
  { path: '/parts',    label: 'Parts',   icon: 'fa-solid fa-cube' },
  { path: '/process',  label: 'Process', icon: 'fa-solid fa-sliders' },
  { path: '/stock',    label: 'Stock',   icon: 'fa-solid fa-warehouse' },
  { path: '/admin',    label: director ? 'Admin' : 'More', icon: director ? 'fa-solid fa-gear' : 'fa-solid fa-ellipsis' },
  ...(phone ? [] : [{ path: '/faq', label: 'FAQ', icon: 'fa-solid fa-circle-question' }]),
];

const BELONGS_TO = [
  ['/insights',   '/admin'],
  ['/financials', '/admin'],
  ['/admin/parts', '/parts'],
];

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)   return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function NotificationPanel({ notifications, onResolve, onClose, topOffset = 52 }) {
  const panelRef = useRef();
  const go = useNavigate();

  useEffect(() => {
    function onClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [onClose]);

  return (
    <div style={np.overlay}>
      <div ref={panelRef} style={{ ...np.panel, top: `${topOffset}px` }}>
        <div style={np.header}>
          <span style={np.title}>Notifications</span>
          <button style={np.closeBtn} onClick={onClose}>×</button>
        </div>
        {notifications.length === 0 ? (
          <div style={np.empty}>No unresolved notifications</div>
        ) : (
          <div style={np.list}>
            {notifications.map(n => (
              <div key={n.id} style={np.item}>
                <div style={np.itemTitle}>{n.title}</div>
                {n.body && <div style={np.itemBody}>{n.body}</div>}
                <div style={np.itemFooter}>
                  <span style={np.itemTime}>{timeAgo(n.created_at)}</span>
                  {n.payload && n.payload.link && (
                    <button style={{ ...np.resolveBtn, marginLeft: 'auto', marginRight: 8 }} onClick={() => { onClose(); go(n.payload.link); }}>Open</button>
                  )}
                  <button style={np.resolveBtn} onClick={() => onResolve(n.id)}>
                    Mark resolved
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const np = {
  overlay:     { position: 'fixed', inset: 0, zIndex: 200, pointerEvents: 'none' },
  panel:       { position: 'absolute', right: '12px', width: '340px', maxWidth: 'calc(100vw - 24px)', maxHeight: '480px', background: '#1e2329', border: '1px solid #2a3040', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden', pointerEvents: 'all' },
  header:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #2a3040' },
  title:       { fontSize: '13px', fontWeight: '700', color: '#f0f2f5' },
  closeBtn:    { background: 'none', border: 'none', color: '#6b7280', fontSize: '18px', cursor: 'pointer', lineHeight: 1, padding: 0 },
  empty:       { padding: '24px 16px', fontSize: '12px', color: '#6b7280', textAlign: 'center' },
  list:        { overflowY: 'auto', flex: 1 },
  item:        { padding: '12px 16px', borderBottom: '1px solid #2a3040' },
  itemTitle:   { fontSize: '12px', fontWeight: '600', color: '#f0f2f5', marginBottom: '3px' },
  itemBody:    { fontSize: '11px', color: '#9ca3af', marginBottom: '8px', lineHeight: '1.5' },
  itemFooter:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  itemTime:    { fontSize: '10px', color: '#4b5563' },
  resolveBtn:  { fontSize: '10px', fontWeight: '600', color: '#14b8a6', background: 'none', border: '1px solid #14b8a6', borderRadius: '6px', padding: '2px 8px', cursor: 'pointer' },
};

// A phone held sideways: the bars took 40% of its height and left the board
// two and a half machines (the director, 12 Oct 2026), so they go slim -- a
// thinner top bar and a bottom menu of icons only.
const isLowNow = () => window.innerHeight <= 500 && window.innerWidth > window.innerHeight;

export default function Nav() {
  const location  = useLocation();
  const { user, logout, can, isDirector, realLevel } = useAuth();
  const navigate  = useNavigate();
  const [isMobile, setIsMobile]         = useState(window.innerWidth < 900);
  const [isLow, setIsLow]               = useState(isLowNow());
  const [notifications, setNotifications] = useState([]);
  const [showPanel, setShowPanel]         = useState(false);
  const [showSwitch, setShowSwitch]       = useState(false);
  const [showViewAs, setShowViewAs]       = useState(false);
  // Suggest a change (the director, 28 Oct 2026): operators and setters, from
  // any page; the button sits in the bar beside the bell, so it never covers
  // the page. { shot, page, startOn } while the panel is open.
  const [suggest, setSuggest]             = useState(null);
  const [taking, setTaking]               = useState(false);
  const suggests = can('suggest');

  useEffect(() => {
    const check = () => { setIsMobile(window.innerWidth < 900); setIsLow(isLowNow()); };
    window.addEventListener('resize', check);
    window.visualViewport?.addEventListener('resize', check);
    return () => {
      window.removeEventListener('resize', check);
      window.visualViewport?.removeEventListener('resize', check);
    };
  }, []);

  function fetchNotifications() {
    api.get('/api/v1/notifications').then(r => setNotifications(r.data || [])).catch(() => {});
  }

  useEffect(() => {
    fetchNotifications();
    const t = setInterval(fetchNotifications, 60_000);
    return () => clearInterval(t);
  }, []);

  async function resolveNotification(id) {
    try {
      await api.patch(`/api/v1/notifications/${id}/resolve`);
      setNotifications(n => n.filter(x => x.id !== id));
    } catch {}
  }

  // The bell's answer to a suggestion opens the person's own list.
  useEffect(() => {
    if (!suggests || !new URLSearchParams(location.search).has('suggestions')) return;
    setSuggest({ shot: undefined, page: { path: location.pathname }, startOn: 'mine' });
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, suggests, navigate]);

  async function openSuggest() {
    if (suggest) { setSuggest(null); return; }
    setShowPanel(false);
    setTaking(true);
    const page = { path: location.pathname + location.search, title: (document.querySelector('main h1') || document.querySelector('h1'))?.innerText?.trim() || null };
    let shot = null;
    try { shot = await takePicture(); } catch (e) { /* sent without a picture; the panel says so */ }
    setTaking(false);
    setSuggest({ shot, page, startOn: 'new' });
  }

  const unresolved = notifications.length;
  const links      = linksFor(isDirector(), isMobile);
  // Pages opened from a tab that live outside its address: Insights and
  // Financials are Admin's (the director, 7 Oct 2026). A page that belongs to
  // no tab -- the account page -- lights none, rather than claiming Today.
  const owner = BELONGS_TO.find(([p]) => location.pathname.startsWith(p))?.[1];
  const activePath = owner || links.find(l => location.pathname.startsWith(l.path))?.path || null;

  function BellButton({ style: extraStyle = {} }) {
    return (
      <button
        style={{ ...n.bellBtn, ...extraStyle }}
        onClick={() => setShowPanel(p => !p)}
        title="Notifications"
      >
        <i className="fa-solid fa-bell" style={{ fontSize: '14px', color: unresolved > 0 ? '#f59e0b' : '#6b7280' }} />
        {unresolved > 0 && (
          <span style={n.bellBadge}>{unresolved > 9 ? '9+' : unresolved}</span>
        )}
      </button>
    );
  }

  // A plain function, not a component: the bar redraws often, and the button
  // must stay the same button.
  function suggestButton(label) {
    return (
      <button type="button" onClick={openSuggest} disabled={taking} aria-expanded={!!suggest}
        title="Suggest a change to this page: the director gets a picture of it and what you write"
        style={{ ...n.suggestBtn, ...(suggest ? n.suggestOn : {}) }}>
        <i className={taking ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-lightbulb'} style={{ fontSize: '13px' }} aria-hidden="true" />
        {label ? <span>{taking ? 'Taking a picture…' : 'Suggest'}</span> : <span style={n.srOnly}>Suggest a change</span>}
      </button>
    );
  }
  const suggestPanel = (top, phone) => suggest && (
    <SuggestPanel key={suggest.startOn + (suggest.shot ? 'p' : '')} shot={suggest.shot} page={suggest.page} startOn={suggest.startOn}
      top={top} phone={phone} onClose={() => setSuggest(null)} />
  );

  if (isMobile) {
    const topH = isLow ? 32 : 44;
    const bottomH = isLow ? 40 : 64;
    return (
      <>
        <div style={{ height: `${topH}px` }} />
        <div style={{ ...n.mobileTopBar, height: `${topH}px` }}>
          <span style={n.mobileBrand}><Logo size={15} /></span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {suggests && suggestButton(false)}
            <BellButton />
          </div>
        </div>
        <nav style={{ ...styles.bottomNav, height: `${bottomH}px` }}>
          {links.map(link => {
            const isActive = activePath === link.path;
            return (
              <Link key={link.path} to={link.path}
                title={isLow ? link.label : undefined} aria-label={link.label}
                style={{
                  ...styles.tab,
                  background: isActive ? '#161b22' : 'transparent',
                }}>
                <div style={{ position: 'relative', display: 'inline-flex' }}>
                  <i className={link.icon} style={{
                    fontSize: isLow ? '16px' : '18px',
                    color: isActive ? '#14b8a6' : '#6b7280',
                    marginBottom: isLow ? 0 : '3px',
                  }} />
                </div>
                {!isLow && (
                  <span style={{ ...styles.tabLabel, color: isActive ? '#14b8a6' : '#6b7280' }}>
                    {link.label}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <div style={{ height: `${bottomH}px` }} />
        {showPanel && (
          <NotificationPanel
            notifications={notifications}
            onResolve={resolveNotification}
            onClose={() => setShowPanel(false)}
            topOffset={topH + 4}
          />
        )}
        {suggestPanel(topH + 4, true)}
      </>
    );
  }

  return (
    <>
      <nav style={styles.topNav}>
        <div style={styles.brand}><Logo size={15} /></div>
        <div style={styles.links}>
          {links.map(link => {
            const isActive = activePath === link.path;
            return (
              <Link key={link.path} to={link.path} style={{
                ...styles.link,
                ...(isActive ? styles.active : {}),
              }}>
                <div style={{ position: 'relative', display: 'inline-flex', marginRight: '6px' }}>
                  <i className={link.icon} style={{ fontSize: '13px', color: isActive ? '#14b8a6' : '#6b7280' }} />
                </div>
                {link.label}
              </Link>
            );
          })}
        </div>
        <div style={styles.userArea}>
          {suggests && suggestButton(true)}
          <BellButton />
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', cursor: 'pointer', gap: '2px' }}
            onClick={() => navigate('/account')}
            title="My account">
            <span style={styles.userName}>{user?.name}</span>
            <span style={styles.userRole}>{user?.role}</span>
          </div>
          {realLevel === 'director' && (
            <span style={{ position: 'relative' }}>
              <button style={styles.switchBtn} onClick={() => setShowViewAs(v => !v)} aria-expanded={showViewAs} title="See the app as a Setter or an Operator see it">
                View as
              </button>
              {showViewAs && (
                <div style={{ position: 'absolute', top: '34px', right: 0, zIndex: 900, background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                  <ViewAsPicker onPicked={() => setShowViewAs(false)} />
                </div>
              )}
            </span>
          )}
          <button
            style={styles.switchBtn}
            onClick={() => setShowSwitch(true)}
            title="Hand this device to someone else">
            Switch
          </button>
          <button style={styles.logoutBtn} onClick={logout}>Sign out</button>
        </div>
      </nav>
      {showSwitch && <SwitchUserModal onClose={() => setShowSwitch(false)} />}
      {showPanel && (
        <NotificationPanel
          notifications={notifications}
          onResolve={resolveNotification}
          onClose={() => setShowPanel(false)}
        />
      )}
      {suggestPanel(52, false)}
    </>
  );
}

const n = {
  suggestBtn:   { display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: '1px solid #14b8a655', color: '#14b8a6', borderRadius: '8px', padding: '4px 9px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'system-ui, sans-serif', whiteSpace: 'nowrap' },
  suggestOn:    { background: '#14b8a61f', borderColor: '#14b8a6' },
  srOnly:       { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
  bellBtn:      { position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  bellBadge:    { position: 'absolute', top: '-3px', right: '-3px', background: '#ef4444', color: '#fff', fontSize: '9px', fontWeight: '700', borderRadius: '8px', padding: '1px 4px', lineHeight: '1.4', minWidth: '14px', textAlign: 'center' },
  mobileTopBar: { position: 'fixed', top: 0, left: 0, right: 0, height: '44px', background: '#1e2329', borderBottom: '1px solid #2a3040', zIndex: 101, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px' },
  mobileBrand:  { display: 'inline-flex', alignItems: 'center' },
};

const styles = {
  topNav:    { background: '#1e2329', borderBottom: '1px solid #2a3040', padding: '0 16px', display: 'flex', alignItems: 'center', height: '48px', position: 'sticky', top: 0, zIndex: 100, fontFamily: 'system-ui, sans-serif' },
  brand:     { display: 'flex', alignItems: 'center', paddingRight: '16px', borderRight: '1px solid #2a3040', marginRight: '16px', whiteSpace: 'nowrap' },
  links:     { display: 'flex', gap: '0', flex: 1, overflow: 'hidden' },
  link:      { fontSize: '12px', fontWeight: '500', color: '#6b7280', padding: '0 14px', height: '48px', display: 'flex', alignItems: 'center', textDecoration: 'none', borderBottom: '2px solid transparent', fontFamily: 'system-ui, sans-serif', whiteSpace: 'nowrap' },
  active:    { color: '#f0f2f5', borderBottom: '2px solid #14b8a6' },
  userArea:  { display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto', flexShrink: 0 },
  userName:  { fontSize: '12px', fontWeight: '600', color: '#f0f2f5', whiteSpace: 'nowrap' },
  userRole:  { fontSize: '10px', color: '#6b7280', background: '#2a3040', padding: '2px 8px', borderRadius: '8px', whiteSpace: 'nowrap' },
  logoutBtn: { background: 'none', border: '1px solid #2a3040', color: '#6b7280', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' },
  switchBtn: { background: 'none', border: '1px solid #2a3040', color: '#14b8a6', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' },
  badge:     { position: 'absolute', top: '-5px', right: '-7px', background: '#ef4444', color: '#fff', fontSize: '9px', fontWeight: '700', borderRadius: '8px', padding: '1px 4px', lineHeight: '1.4', minWidth: '14px', textAlign: 'center' },
  bottomNav: { position: 'fixed', bottom: 0, left: 0, right: 0, height: '64px', background: '#1e2329', borderTop: '1px solid #2a3040', zIndex: 100, display: 'flex', flexDirection: 'row' },
  tab:       { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', gap: '2px', padding: '4px 0' },
  tabLabel:  { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'system-ui, sans-serif' },
};
